import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { DropdownMenu } from '@abyss/web/ui/DropdownMenu';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useSessionStorage } from 'usehooks-ts';

import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import {
  Constants,
  FilterCollectionModel,
  FilterOptionsType,
} from '../Constants';
import { phoneOnly } from '../ConstantsStyles';
import { DistanceInfoIcon } from '../RadioGroup/DistanceInfoIcon';

const Container = styled('div', {
  background: '$primary2',
  marginTop: '8px',
  padding: '10px 20px 10px 0px',
});

type Options = {
  label: String | ReactNode;
  value: string | number | boolean;
  dnt_label: string;
};
interface Props {
  defaultValue?: string | number | boolean;
  disableSubmitBtn?: boolean;
  isDisabled?: boolean;
  locationForAnalytics?: string;
  model: string;
  onApply?: (value: string | number | boolean) => void;
  onOptionChange?: (any) => void;
  options: Array<Options>;
  submitBtnName?: string;
}

export const FilterDropDown = ({
  defaultValue,
  isDisabled,
  locationForAnalytics,
  model,
  onApply = () => {},
  onOptionChange = () => {},
  options,
  submitBtnName,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);

  const [dropdownValue, setDropdownValue] = useState(
    defaultValue &&
      options.filter((option) => option.value === defaultValue).length > 0
      ? String(defaultValue)
      : ''
  );

  const CustomDropDown = styled(DropdownMenu, {
    '&.abyss-dropdown-menu-trigger': {
      width: '100%',
    },
    '&.abyss-insert-element-root': {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    '&.abyss-dropdown-menu-content-list': {
      width: 'auto',
      minWidth: '350px',
      '@screen < $sm': {
        minWidth: '280px',
      },
    },
  });

  const buttonStyles: any = {
    'abyss-button-root': {
      height: '42px',
      marginTop: '20px',
      border: 'none',
      borderRadius: '24px',
      width: mobileScreen ? '100%' : '',
      fontWeight: 600,
    },
  };

  useEffect(() => {
    setDropdownValue(
      defaultValue &&
        options.filter((option) => option.value === defaultValue).length > 0
        ? String(defaultValue)
        : ''
    );
  }, [defaultValue]);

  function onChangeDropdownValue(val) {
    if (val) {
      adobeLinkTrackEvent({
        name: options.filter((option) => val === option.value.toString())?.[0]
          ?.dnt_label,
        location: locationForAnalytics,
        type: FilterOptionsType.DROPDOWN,
      });
    }
    setDropdownValue(val);
    onOptionChange({
      [model]: options.filter((option) => val === option.value),
    });
  }
  const [defaultvalue, setDefaultvalue] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.DEFAULT_SEARCH_FILTERS,
    {}
  );

  const onSubmit = () => {
    onApply(dropdownValue);
    setDefaultvalue({ ...defaultvalue, autoSearchRadius: dropdownValue });
  };

  const sortedOptions = options.sort((a, b) => a?.c_value - b?.c_value);
  const selectedOption = sortedOptions.find(
    (option) => option.value === defaultValue
  );
  const isDistance = model === FilterCollectionModel.DISTANCE;

  if (isDisabled) {
    const tooltipContent = isDistance
      ? t('CONTENT_FILTERS.DISTANCE_DISABLED_TOOLTIP')
      : t('CONTENT_FILTERS.GENERIC_FILTER_DISABLED_TOOLTIP');

    return (
      <Tooltip content={tooltipContent}>
        <Container
          data-auto-testid="filter-dropdown-section"
          data-testid="filter-dropdown-section"
        >
          <CustomDropDown
            after={<IconMaterial icon="keyboard_arrow_down" />}
            data-testid="filter-dropdown-menu-disabled"
            isDisabled={isDisabled}
            label={t('Not applicable')}
            outline
          />
        </Container>
      </Tooltip>
    );
  }

  return (
    <Container
      data-auto-testid="filter-dropdown-section"
      data-testid="filter-dropdown-section"
    >
      <CustomDropDown
        after={<IconMaterial icon="keyboard_arrow_down" />}
        css={
          !selectedOption && {
            '&.abyss-insert-element-root': {
              marginLeft: '90%',
            },
          }
        }
        data-testid="filter-dropdown-menu"
        label={selectedOption ? selectedOption?.label : ''}
        menuItems={sortedOptions.map((option) => ({
          title: option.label?.toString(),
          onClick: () => {
            onChangeDropdownValue(option.value);
          },
        }))}
        outline
      />
      {isDistance ? (
        <div style={{ marginTop: '8px' }}>
          <DistanceInfoIcon />
        </div>
      ) : null}
      {submitBtnName ? (
        <Button
          css={buttonStyles}
          data-auto-testid="apply-button"
          data-testid={`${model}-apply-button`}
          onClick={onSubmit}
          type="submit"
        >
          {submitBtnName}
        </Button>
      ) : null}
    </Container>
  );
};
